import React, { createContext, useContext, useState, useEffect } from 'react';
import { darkTheme, beigeTheme, whiteTheme } from '../themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentThemeName, setCurrentThemeName] = useState('dark');
  const [theme, setTheme] = useState(darkTheme);
  const [isToggling, setIsToggling] = useState(false);

  const themes = {
    dark: darkTheme,
    light: whiteTheme,
    beige: beigeTheme
  };

  // Get the actual theme object based on current theme name
  const getActualTheme = (themeName) => {
    return themes[themeName] || themes.dark;
  };

  // Get display name for the theme
  const getThemeDisplayName = (themeName) => {
    switch (themeName) {
      case 'dark': return 'Dark';
      case 'light': return 'Light';
      case 'beige': return 'Beige';
      default: return 'Dark';
    }
  };

  // Get next theme in cycle
  const getNextTheme = (currentTheme) => {
    const themeOrder = ['dark', 'light', 'beige'];
    const currentIndex = themeOrder.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    return themeOrder[nextIndex];
  };

  useEffect(() => {
    // Load theme preference from localStorage
    const savedTheme = localStorage.getItem('oaklandAI-theme');
    console.log('Loading theme from localStorage:', savedTheme);
    
    if (savedTheme && themes[savedTheme]) {
      console.log('Setting theme:', savedTheme);
      setCurrentThemeName(savedTheme);
      const actualTheme = getActualTheme(savedTheme);
      setTheme(actualTheme);
      updateBodyClass(savedTheme);
    } else {
      console.log('Setting default dark theme');
      setCurrentThemeName('dark');
      setTheme(themes.dark);
      updateBodyClass('dark');
    }
  }, []);

  // Update body class for CSS theme overrides
  const updateBodyClass = (themeName) => {
    console.log('Updating body class for theme:', themeName);
    if (themeName === 'dark') {
      document.body.className = '';
      document.body.setAttribute('data-theme', 'dark');
    } else if (themeName === 'light') {
      document.body.className = 'theme-light';
      document.body.setAttribute('data-theme', 'light');
    } else if (themeName === 'beige') {
      document.body.className = 'theme-beige';
      document.body.setAttribute('data-theme', 'beige');
    }
    console.log('Body class set to:', document.body.className);
    console.log('Body data-theme set to:', document.body.getAttribute('data-theme'));
  };

  // Ensure theme and currentThemeName are always in sync
  useEffect(() => {
    console.log('Theme state changed - currentThemeName:', currentThemeName, 'theme.primaryBg:', theme.primaryBg);
    
    // Verify theme consistency
    const expectedTheme = getActualTheme(currentThemeName);
    if (theme.primaryBg !== expectedTheme.primaryBg) {
      console.warn('Theme inconsistency detected, fixing...');
      setTheme(expectedTheme);
    }
  }, [currentThemeName, theme]);

  const toggleTheme = () => {
    // Prevent rapid clicking
    if (isToggling) {
      console.log('Theme toggle already in progress, ignoring click');
      return;
    }
    
    console.log('=== THEME TOGGLE DEBUG ===');
    console.log('toggleTheme function called');
    console.log('Current theme:', currentThemeName);
    console.log('Current theme.primaryBg:', theme.primaryBg);
    
    setIsToggling(true);
    
    try {
      const nextThemeName = getNextTheme(currentThemeName);
      console.log('Theme cycle - current:', currentThemeName, 'next:', nextThemeName);
      
      setCurrentThemeName(nextThemeName);
      const actualTheme = getActualTheme(nextThemeName);
      console.log('New theme object:', actualTheme);
      console.log('New theme.primaryBg:', actualTheme.primaryBg);
      setTheme(actualTheme);
      
      // Update localStorage
      localStorage.setItem('oaklandAI-theme', nextThemeName);
      console.log('localStorage set to:', nextThemeName);
      
      // Update body class
      updateBodyClass(nextThemeName);
      
      // Reset toggle flag after a short delay
      setTimeout(() => {
        setIsToggling(false);
      }, 100);
      
      console.log('Theme toggle completed');
      console.log('=== END DEBUG ===');
    } catch (error) {
      console.error('Error during theme toggle:', error);
      setIsToggling(false);
    }
  };

  const setBeigeTheme = () => {
    console.log('Manually setting beige theme for testing');
    setCurrentThemeName('beige');
    const actualTheme = getActualTheme('beige');
    setTheme(actualTheme);
    localStorage.setItem('oaklandAI-theme', 'beige');
    updateBodyClass('beige');
  };

  const value = {
    theme,
    currentThemeName,
    toggleTheme,
    isToggling,
    getThemeDisplayName,
    getNextTheme,
    setBeigeTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 